import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import WYSIWYGEditor from '@/components/ui/wysiwyg';
import type { LocalImageMetadata } from '@/components/ui/wysiwyg/context/task-attempt-context';
import { Maximize2 } from 'lucide-react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { defineModal } from '@/lib/modals';

export interface DescriptionExpandDialogProps {
  /** Current description value (markdown string) */
  value: string;
  /** Callback when description changes */
  onChange: (value: string) => void;
  /** Project ID for WYSIWYG file search */
  projectId: string;
  /** Task ID for image resolution (optional, for edit mode) */
  taskId?: string;
  /** Local images for immediate rendering */
  localImages?: LocalImageMetadata[];
  /** Callback when files are pasted */
  onPasteFiles?: (files: File[]) => void;
  /** Whether the editor is disabled */
  disabled?: boolean;
  /** Whether this is edit mode (vs create mode) */
  editMode?: boolean;
}

export type DescriptionExpandResult = 'saved' | 'canceled';

const DescriptionExpandDialogImpl = NiceModal.create<DescriptionExpandDialogProps>(
  ({
    value,
    onChange,
    projectId,
    taskId,
    localImages,
    onPasteFiles,
    disabled = false,
    editMode = false,
  }) => {
    const modal = useModal();
    const { t } = useTranslation('tasks');

    // Local state for the description while editing in expand mode
    const [expandedValue, setExpandedValue] = useState(value);

    // Handle save - apply changes to parent form
    const handleSave = useCallback(() => {
      onChange(expandedValue);
      modal.resolve('saved' as DescriptionExpandResult);
      modal.hide();
    }, [expandedValue, onChange, modal]);

    // Handle cancel - discard changes
    const handleCancel = useCallback(() => {
      setExpandedValue(value); // Reset to original value
      modal.resolve('canceled' as DescriptionExpandResult);
      modal.hide();
    }, [value, modal]);

    // Handle dialog close
    const handleOpenChange = useCallback(
      (open: boolean) => {
        if (!open) {
          handleCancel();
        }
      },
      [handleCancel]
    );

    // Handle Cmd+Enter to save
    const handleCmdEnter = useCallback(() => {
      handleSave();
    }, [handleSave]);

    return (
      <Dialog open={modal.visible} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[800px] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {editMode
                ? t('taskFormDialog.expandDialog.editTitle')
                : t('taskFormDialog.expandDialog.createTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="border rounded-md p-3 min-h-[400px] max-h-[500px] overflow-auto">
            <WYSIWYGEditor
              placeholder={t('taskFormDialog.descriptionPlaceholder')}
              className="w-full min-h-[380px]"
              value={expandedValue}
              onChange={(desc) => setExpandedValue(desc)}
              disabled={disabled}
              projectId={projectId}
              onPasteFiles={onPasteFiles}
              onCmdEnter={handleCmdEnter}
              taskId={taskId}
              localImages={localImages}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={disabled}
            >
              {t('taskFormDialog.expandDialog.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={disabled}>
              {t('taskFormDialog.expandDialog.apply')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
);

export const DescriptionExpandDialog = defineModal<
  DescriptionExpandDialogProps,
  DescriptionExpandResult
>(DescriptionExpandDialogImpl);

/**
 * Button component to open the expand dialog
 */
export function ExpandDescriptionButton({
  onClick,
  disabled = false,
}: {
  onClick: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation('tasks');

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      className="h-8 px-2 text-xs"
      title={t('taskFormDialog.expandButton.tooltip')}
    >
      <Maximize2 className="h-4 w-4 mr-1" />
      {t('taskFormDialog.expandButton.label')}
    </Button>
  );
}